import React, { useState } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

export default function TagEditor({ tags, onTagsChange }) {
  const [editingTag, setEditingTag] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    explanation: '',
    search_terms: '',
    priority: 'medium',
    confidence_threshold: 0.75
  });

  const handleToggleTag = (tagId) => {
    const updatedTags = tags.map(tag => 
      tag.id === tagId ? { ...tag, enabled: !tag.enabled } : tag
    );
    onTagsChange(updatedTags);
  };

  const handleEditTag = (tag) => {
    setEditingTag({
      ...tag,
      search_terms: (tag.search_terms || []).join(', ')
    });
  };

  const handleSaveEdit = () => {
    const updatedTags = tags.map(tag => 
      tag.id === editingTag.id 
        ? {
            ...editingTag,
            search_terms: editingTag.search_terms.split(',').map(term => term.trim()).filter(term => term)
          }
        : tag
    );
    onTagsChange(updatedTags);
    setEditingTag(null);
  };

  const handleDeleteTag = (tagId) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      const updatedTags = tags.filter(tag => tag.id !== tagId);
      onTagsChange(updatedTags);
    }
  };

  const handleAddTag = () => {
    if (!newTag.name.trim()) return;
    
    const tagToAdd = {
      id: `tag_${Date.now()}`,
      name: newTag.name.trim(),
      explanation: newTag.explanation.trim(),
      search_terms: newTag.search_terms.split(',').map(term => term.trim()).filter(term => term),
      priority: newTag.priority,
      confidence_threshold: parseFloat(newTag.confidence_threshold),
      enabled: true
    };
    
    onTagsChange([...tags, tagToAdd]);
    setNewTag({
      name: '',
      explanation: '',
      search_terms: '',
      priority: 'medium',
      confidence_threshold: 0.75
    });
    setShowAddForm(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{tags.filter(tag => tag.enabled).length}</span> active tags
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">{tags.filter(tag => !tag.enabled).length}</span> disabled
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Tag
        </button>
      </div>

      {/* Add new tag form */}
      {showAddForm && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-3">Add New Discovery Tag</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag Name *
              </label>
              <input
                type="text"
                placeholder="e.g., John Doe Medical Records"
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={newTag.priority}
                onChange={(e) => setNewTag({ ...newTag, priority: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Explanation
            </label>
            <textarea
              placeholder="Explain why this tag is important for discovery..."
              value={newTag.explanation}
              onChange={(e) => setNewTag({ ...newTag, explanation: e.target.value })}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Terms (comma-separated)
              </label>
              <input
                type="text"
                placeholder="medical, injury, treatment, doctor"
                value={newTag.search_terms}
                onChange={(e) => setNewTag({ ...newTag, search_terms: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confidence Threshold
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={newTag.confidence_threshold}
                onChange={(e) => setNewTag({ ...newTag, confidence_threshold: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleAddTag}
              disabled={!newTag.name.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              Add Tag
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewTag({
                  name: '',
                  explanation: '',
                  search_terms: '',
                  priority: 'medium',
                  confidence_threshold: 0.75
                });
              }}
              className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tags list */}
      <div className="space-y-3">
        {tags.map((tag) => (
          <div key={tag.id} className={`border rounded-lg p-4 transition-all ${
            tag.enabled 
              ? 'border-green-200 bg-green-50' 
              : 'border-gray-200 bg-gray-50 opacity-75'
          }`}>
            
            {/* Tag header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleToggleTag(tag.id)}
                  className={`p-1 rounded transition-colors ${
                    tag.enabled 
                      ? 'text-green-600 hover:bg-green-100' 
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={tag.enabled ? 'Disable tag' : 'Enable tag'}
                >
                  {tag.enabled ? (
                    <EyeIcon className="w-4 h-4" />
                  ) : (
                    <EyeSlashIcon className="w-4 h-4" />
                  )}
                </button>
                
                <span className="font-medium text-gray-900">{tag.name}</span>
                
                <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(tag.priority)}`}>
                  {tag.priority}
                </span>
                
                <span className="text-xs text-gray-500">
                  {Math.round((tag.confidence_threshold || 0) * 100)}% threshold
                </span>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleEditTag(tag)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Edit tag"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                
                <button
                  onClick={() => handleDeleteTag(tag.id)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete tag"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Tag explanation */}
            <p className="text-sm text-gray-700 mb-3">{tag.explanation}</p>
            
            {/* Search terms */}
            <div className="flex flex-wrap gap-1">
              {(tag.search_terms || []).slice(0, 10).map((term, idx) => (
                <span key={idx} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                  {term}
                </span>
              ))}
              {(tag.search_terms?.length || 0) > 10 && (
                <span className="text-xs text-gray-500 px-2 py-1">
                  +{tag.search_terms.length - 10} more
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editingTag && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Discovery Tag</h3>
              <button
                onClick={() => setEditingTag(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tag Name
                </label>
                <input
                  type="text"
                  value={editingTag.name}
                  onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation
                </label>
                <textarea
                  value={editingTag.explanation}
                  onChange={(e) => setEditingTag({ ...editingTag, explanation: e.target.value })}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={editingTag.priority}
                    onChange={(e) => setEditingTag({ ...editingTag, priority: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confidence Threshold
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={editingTag.confidence_threshold}
                    onChange={(e) => setEditingTag({ ...editingTag, confidence_threshold: parseFloat(e.target.value) })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Terms (comma-separated)
                </label>
                <input
                  type="text"
                  value={editingTag.search_terms}
                  onChange={(e) => setEditingTag({ ...editingTag, search_terms: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setEditingTag(null)}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
        <div className="flex items-start">
          <InformationCircleIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Tips:</strong> Tags help identify relevant documents during discovery. Use descriptive names and include specific search terms. 
            Higher priority tags are processed first. Confidence thresholds control how strict the matching should be.
          </div>
        </div>
      </div>
    </div>
  );
}